/**
 * Mouse-resizable columns for the plain HTML tables of wave planning; the AG
 * Grid grids resize on their own. Widths are px, applied through a
 * ``<colgroup>`` with ``table-layout: fixed``: the table is as wide as its
 * columns, and when its container is wider the ``fill`` columns the user hasn't
 * resized share the rest (like flex columns). Widths last for the page's life.
 */
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'

export interface SizedColumn {
  key: string
  /** Default width (px). */
  width: number
  /** Smallest width a drag can reach (px). */
  minWidth?: number
}

const DEFAULT_MIN = 60

/** Rendered widths: the user's or the default, plus the fill share of the rest. */
export function columnWidths(
  columns: SizedColumn[],
  custom: Record<string, number>,
  available: number,
  fill: readonly string[] = []
): Record<string, number> {
  const widths: Record<string, number> = {}
  for (const column of columns)
    widths[column.key] = custom[column.key] ?? column.width
  const sum = columns.reduce((total, column) => total + widths[column.key], 0)
  const growing = fill.filter(key => key in widths && !(key in custom))
  const extra = Math.floor(available - sum)
  if (extra > 0 && growing.length) {
    const share = Math.floor(extra / growing.length)
    growing.forEach((key, index) => {
      // The first fill column takes the rounding remainder.
      widths[key] += share + (index === 0 ? extra - share * growing.length : 0)
    })
  }
  return widths
}

/** The user's widths by column key (only the columns they resized). */
export type ColumnWidthsState = [
  Record<string, number>,
  Dispatch<SetStateAction<Record<string, number>>>,
]

/**
 * ``state``: keep the user's widths in a parent that outlives the table (e.g.
 * across the DC page's waves, whose grid remounts per wave); default: this table.
 */
export function useColumnSizing(
  columns: SizedColumn[],
  fill: readonly string[] = [],
  state?: ColumnWidthsState
) {
  const own = useState<Record<string, number>>({})
  const [custom, setCustom] = state ?? own
  const [available, setAvailable] = useState(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element) return
    const measure = () => setAvailable(element.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  const widths = useMemo(
    () => columnWidths(columns, custom, available, fill),
    [columns, custom, available, fill]
  )
  const tableWidth = columns.reduce(
    (total, column) => total + widths[column.key],
    0
  )
  const minimum = useCallback(
    (key: string) =>
      columns.find(column => column.key === key)?.minWidth ?? DEFAULT_MIN,
    [columns]
  )
  const resize = useCallback(
    (key: string, width: number) =>
      setCustom(current => ({
        ...current,
        [key]: Math.max(minimum(key), Math.round(width)),
      })),
    [minimum, setCustom]
  )
  const reset = useCallback(
    (key: string) =>
      setCustom(current => {
        const next = { ...current }
        delete next[key]
        return next
      }),
    [setCustom]
  )
  return { containerRef, widths, tableWidth, resize, reset }
}
